// src/components/booking/AppointmentsManagerModal.tsx
'use client';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
  Spinner,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'react-toastify';
import useCustomerStore from '@/store/useCustomerStore';
import type { Client } from '@/utils/types';
import { sendEmail, createAppointmentCancellationEmailTemplate } from '@/lib/send-email';
import { sendSiteFormEmail, idNumerico } from '@/lib/site-form-email';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { tDelTenant } from '@/i18n/delTenant';
import { horaDelPais } from '@/lib/country';
import {
  businessTimezone, todayYMDIn, nowMinutesIn, labelFromInstant, ymdFromInstant,
  bookingDateLocale, fill,
} from './bookingShared';

/* ==== Utils tiempo (sin alterar TZ) ==== */
function labelFromTimestamp(ts: unknown): string {
  if (!ts) return '';
  if (typeof ts === 'string') {
    const s = ts.replace(' ', 'T');
    const m = s.match(/(?:T| )(\d{2}:\d{2})(?::\d{2})?/);
    if (m) return m[1];
    const m2 = s.match(/\b([01]\d|2[0-3]):([0-5]\d)\b/);
    return m2 ? `${m2[1]}:${m2[2]}` : '';
  }
  if (ts instanceof Date) {
    const iso = ts.toISOString();
    const m = iso.match(/T(\d{2}:\d{2})(?::\d{2})?/);
    return m ? m[1] : '';
  }
  if (typeof ts === 'number') {
    const iso = new Date(ts).toISOString();
    const m = iso.match(/T(\d{2}:\d{2})(?::\d{2})?/);
    return m ? m[1] : '';
  }
  return '';
}
function hhmmToMinutes(hhmm: string) {
  const m = hhmm.match(/(\d{2}):(\d{2})/);
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
}
/**
 * ¿La cita todavía no empezó? Se compara contra el reloj de la SUCURSAL, no el
 * de quien mira: `slot_start` es hora de pared, y el dueño de la cita puede
 * estar en otro huso — justamente el caso de alguien de viaje que quiere
 * cancelar. Con el reloj local, a un cliente en Europa se le escondía el botón
 * Cancelar de citas que todavía no habían pasado.
 */
function isFutureSlot(ymd: string, slotStart: string, tz: string) {
  const today = todayYMDIn(tz);
  if (ymd > today) return true;
  if (ymd < today) return false;
  const startHM = hhmmToMinutes(labelFromTimestamp(slotStart));
  return startHM > nowMinutesIn(tz);
}
/** `locale` opcional: sin él es el del navegador, como siempre; Brasil pasa 'pt-BR'. */
function prettyYMD(ymd?: string, locale?: string) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString(locale);
}

/* ==== Tipos ==== */
// Shape que devuelve fn_my_appointments (solo citas pending/confirmed).
type AppointmentItem = {
  id: number;
  client_id: number;
  dealership_id: number;
  dealership_address: string | null;
  service_name: string | null;
  slot_start: string;
  slot_end: string;
  status: 'pending' | 'confirmed';
  vehicle_id: number | null;
  notes: string | null;
  cancel_token: string;
  /** B4: instante real y zona de la sucursal, si la base ya los ofrece. */
  slot_start_utc?: string | null;
  slot_end_utc?: string | null;
  timezone?: string | null;
};

interface AppointmentsManagerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client | undefined;
}

/* ===================================================================================== */
export default function AppointmentsManagerModal({
  open,
  onOpenChange,
  client,
}: AppointmentsManagerModalProps) {
  const { customer, appointmentTokens } = useCustomerStore();
  // Textos por i18n (en español, idénticos a los de siempre; Brasil en pt) y
  // fechas por PAÍS: 'pt-BR' en Brasil, `undefined` (= lo de siempre) en el resto.
  const { t } = useTranslation();
  const dateLocale = bookingDateLocale(client?.country);
  /** El estado crudo de la base: en español se ve tal cual (como siempre); en pt, traducido. */
  const statusLabel = (s: string) =>
    (s === 'pending' ? t('booking.manager.statusPending')
      : s === 'confirmed' ? t('booking.manager.statusConfirmed')
        : s);
  // Zona del NEGOCIO. Acá no hay sucursal a mano (fn_my_appointments no la
  // expone), así que resuelve por clients.country. Ver bookingShared.
  const tz = useMemo(() => businessTimezone(client), [client]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AppointmentItem[]>([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelId, setCancelId] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const pendingFocusRef = useRef<HTMLTextAreaElement | null>(null);

  /* ----- Listado por tokens de cancelación guardados en este navegador ----- */
  const fetchAppointments = async () => {
    if (!client?.id || appointmentTokens.length === 0) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      // Primero el contrato UTC (`20260826120000`), que además trae la zona de
      // cada sucursal. Si la base todavía no lo tiene, se cae al de siempre.
      const utc = await supabase.rpc('fn_my_appointments_utc', {
        p_tokens: appointmentTokens,
      } as any);
      if (!utc.error) {
        const list = ((utc.data || []) as AppointmentItem[])
          .filter((r) => Number(r.client_id) === Number(client.id))
          .map((r) => ({ ...r, slot_start: r.slot_start_utc!, slot_end: r.slot_end_utc! }))
          // Con instantes, el orden alfabético del ISO ES el cronológico aunque
          // las citas sean de sucursales en husos distintos.
          .sort((a, b) => String(a.slot_start_utc).localeCompare(String(b.slot_start_utc)));
        setRows(list);
        return;
      }

      const { data, error } = await supabase.rpc('fn_my_appointments', {
        p_tokens: appointmentTokens,
      } as any);
      if (error) {
        console.error('fn_my_appointments error:', error);
        toast.error(error.message || t('booking.manager.fetchFailed'));
        setRows([]);
        return;
      }
      const list = ((data || []) as AppointmentItem[])
        .filter((r) => Number(r.client_id) === Number(client.id))
        .sort((a, b) => String(a.slot_start).localeCompare(String(b.slot_start)));
      setRows(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchAppointments();
  }, [open, client?.id, appointmentTokens]);

  /* --- La fecha y la hora de una cita, del instante si está --------------- */
  const zonaDe = (r: AppointmentItem) => r.timezone || tz;
  const ymdDe = (r: AppointmentItem) =>
    (r.slot_start_utc ? ymdFromInstant(r.slot_start_utc, zonaDe(r)) : String(r.slot_start).slice(0, 10));
  // Hora visible: 'HH:mm' como siempre; EE.UU. "2:30 PM" (`horaDelPais`).
  const horaDe = (utcIso: string | null | undefined, pared: string, r: AppointmentItem) =>
    horaDelPais(utcIso ? labelFromInstant(utcIso, zonaDe(r)) : labelFromTimestamp(pared), client?.country);
  const puedeCancelar = (r: AppointmentItem) =>
    (r.slot_start_utc
      ? new Date(r.slot_start_utc).getTime() > Date.now()
      : isFutureSlot(ymdDe(r), r.slot_start, tz));

  const openConfirm = (appt: AppointmentItem) => {
    const can = puedeCancelar(appt);
    if (!can) {
      toast.info(t('booking.manager.pastCannotCancel'));
      return;
    }
    setCancelId(appt.id);
    setCancelReason('');
    setConfirmOpen(true);
    setTimeout(() => pendingFocusRef.current?.focus(), 50);
  };

  const doCancel = async () => {
    if (!cancelId || !client?.id) return;

    // Buscar los datos de la cita antes de cancelarla
    const appointment = rows.find(r => r.id === cancelId);
    if (!appointment) {
      toast.error(t('booking.manager.notFound'));
      return;
    }

    setCancelling(true);
    try {
      // Cancelar la cita por token (RETURNS boolean: true = canceló)
      const { data, error } = await supabase.rpc('fn_appointments_cancel_by_token', {
        p_cancel_token: appointment.cancel_token,
        p_reason: cancelReason.trim() || null,
      } as any);
      if (error) {
        console.error('Cancel RPC error:', error);
        throw error;
      }
      if (data !== true) {
        toast.warning(t('booking.manager.cancelFailedMaybe'));
        setConfirmOpen(false);
        setCancelId(null);
        setCancelReason('');
        fetchAppointments();
        return;
      }

      console.log('📧 Cita cancelada, enviando notificaciones por email...');

      // Enviar notificaciones por email. El cuerpo es el mismo para el cliente y
      // para la automotora: va en el idioma del TENANT (como la plantilla, que
      // elige por país), igual que los asuntos de la automotora y del vendedor.
      const tAutomotora = tDelTenant(client?.country);
      const ymd = ymdDe(appointment);
      const emailData = {
        customerName: `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() || tAutomotora('booking.email.customerFallback'),
        customerEmail: customer?.email || '',
        customerPhone: customer?.phone || '',
        appointmentDate: prettyYMD(ymd, dateLocale),
        appointmentTime: `${horaDe(appointment.slot_start_utc, appointment.slot_start, appointment)} - ${horaDe(appointment.slot_end_utc, appointment.slot_end, appointment)}`,
        dealershipAddress: appointment.dealership_address || fill(tAutomotora('booking.common.branchNumber'), { id: String(appointment.dealership_id) }),
        vehicleDetails: {
          // fn_my_appointments no expone marca/modelo/año (tampoco llegaban antes).
          brand: 'N/A',
          model: 'N/A',
          year: 'N/A',
        },
        cancellationReason: cancelReason.trim() || undefined,
        // La plantilla sale en portugués para Brasil y la de siempre para el resto.
        country: client?.country ?? null,
      };

      const emailContent = createAppointmentCancellationEmailTemplate(emailData);

      // El correo al cliente lo manda el backend (outbox `appointment_communications`),
      // según el aviso de cada sucursal. Desde el navegador ya no se puede: send-email
      // no acepta envíos sin sesión.

      // 2. Enviar email a la automotora
      if (client?.contact?.email) {
        console.log('📤 Enviando notificación de cancelación a la automotora');
        // Destinatario/asunto/HTML los arma la edge (`site_form`); si no
        // puede, cae a este mismo envío de siempre.
        const dealershipEmailResult = await sendSiteFormEmail(
          {
            client_id: idNumerico(client?.id) ?? 0,
            kind: 'cancel_dealer',
            vehicle_id: idNumerico(appointment.vehicle_id),
            // Asunto: booking.email.cancelledSubject con {brand, model} de params.vehicleDetails.
            params: emailData,
          },
          {
            to: [client.contact.email],
            subject: fill(tAutomotora('booking.email.cancelledSubject'), { brand: emailData.vehicleDetails.brand, model: emailData.vehicleDetails.model }),
            content: emailContent,
          },
          sendEmail
        );

        if (dealershipEmailResult.success) {
          console.log('✅ Notificación enviada a la automotora');
        } else {
          console.error('❌ Error al enviar notificación a la automotora:', dealershipEmailResult.error);
        }
      }

      // 3. Avisar al vendedor del vehículo. La edge resuelve desde vehicle_id si tiene
      // vendedor y cuál es su casilla (`site_form`): anon ya no puede leer `users`
      // (pentest 30-oct). Sin vendedor, la edge lo saltea.
      if (appointment.vehicle_id) {
        try {
          const sellerEmailResult = await sendSiteFormEmail(
            {
              client_id: idNumerico(client?.id) ?? 0,
              kind: 'cancel_seller',
              vehicle_id: idNumerico(appointment.vehicle_id),
              // Asunto: booking.email.cancelledSellerSubject con {brand, model} de params.vehicleDetails.
              params: emailData,
            },
            null
          );

          if (sellerEmailResult.success) {
            console.log('✅ Notificación enviada al vendedor');
          } else {
            console.error('❌ Error al enviar notificación al vendedor:', sellerEmailResult.error);
          }
        } catch (error) {
          console.error('Error al notificar al vendedor:', error);
        }
      }

      toast.success(t('booking.manager.cancelled'));
      setConfirmOpen(false);
      setCancelId(null);
      setCancelReason('');
      fetchAppointments();
    } catch (e: any) {
      toast.error(e?.message || t('booking.manager.cancelFailed'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      {/* LISTADO */}
      <Modal
        isOpen={open}
        onOpenChange={onOpenChange}
        size="lg"
        backdrop="blur"
        className="sm:max-h-[88vh] sm:my-6"
        classNames={{
          base: 'm-0 sm:m-4',
          wrapper: 'items-end sm:items-center',
          header: 'px-4 sm:px-6 py-3 sm:py-4',
          body: 'px-4 sm:px-6 py-3 sm:py-4',
          footer: 'px-4 sm:px-6 py-3 sm:py-4',
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex items-start gap-2">
                <div className="inline-flex items-center gap-2 text-base sm:text-lg font-semibold">
                  <Icon icon="mdi:calendar-account" className="text-[18px]" />
                  {t('booking.manager.title')}
                </div>
                <span className="ml-auto text-[11px] sm:text-xs text-gray-500">{tz}</span>
              </ModalHeader>

              <ModalBody className="space-y-4 sm:space-y-6 overflow-y-auto max-h-[75vh] sm:max-h-[60vh]">
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <Spinner />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-sm text-gray-500">{t('booking.manager.empty')}</div>
                ) : (
                  <div className="space-y-3">
                    {rows.map((r) => {
                      const ymd = ymdDe(r);
                      const can = puedeCancelar(r);

                      return (
                        <div
                          key={r.id}
                          className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-4 flex flex-col gap-2"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <Chip
                              variant="flat"
                              className="gap-1.5 inline-flex items-center"
                              startContent={<Icon icon="mdi:calendar-clock" className="text-[18px]" />}
                            >
                              {prettyYMD(ymd, dateLocale)} · {horaDe(r.slot_start_utc, r.slot_start, r)}–
                              {horaDe(r.slot_end_utc, r.slot_end, r)}
                            </Chip>

                            <Chip
                              variant="flat"
                              className="gap-1.5 inline-flex items-center"
                              startContent={<Icon icon="mdi:store-marker-outline" className="text-[18px]" />}
                            >
                              {r.dealership_address || fill(t('booking.common.branchNumber'), { id: String(r.dealership_id) })}
                            </Chip>

                            <Chip
                              variant="flat"
                              className="gap-1.5 inline-flex items-center"
                              startContent={<Icon icon="mdi:progress-check" className="text-[18px]" />}
                            >
                              {statusLabel(r.status)}
                            </Chip>
                          </div>

                          {r.notes && (
                            <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                              <span className="inline-flex items-center gap-1.5 font-medium">
                                <Icon icon="mdi:note-text-outline" className="text-[16px]" />
                                {t('booking.manager.note')}
                              </span>{' '}
                              {r.notes}
                            </div>
                          )}

                          <div className="flex items-center justify-end gap-2 pt-1">
                            <Button
                              size="sm"
                              variant="light"
                              startContent={<Icon icon="mdi:information-outline" className="text-[18px]" />}
                              onPress={() => {
                                toast.info(t('booking.manager.rescheduleHelp'));
                              }}
                            >
                              {t('booking.manager.help')}
                            </Button>
                            {can && (
                              <Button
                                size="sm"
                                color="danger"
                                variant="flat"
                                startContent={<Icon icon="mdi:calendar-remove" className="text-[18px]" />}
                                onPress={() => openConfirm(r)}
                              >
                                {t('booking.common.cancelAppointment')}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ModalBody>

              <ModalFooter>
                <Button variant="light" onPress={() => onOpenChange(false)}>
                  {t('booking.common.close')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Confirmación */}
      <Modal
        isOpen={confirmOpen}
        onOpenChange={setConfirmOpen}
        size="md"
        backdrop="opaque"
        classNames={{ wrapper: 'items-center' }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex items-center gap-2">
                <Icon icon="mdi:alert-decagram-outline" className="text-[20px] text-rose-600" />
                {t('booking.manager.confirmTitle')}
              </ModalHeader>
              <ModalBody className="space-y-3">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('booking.manager.confirmText')}
                </p>
                <textarea
                  ref={pendingFocusRef}
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value.slice(0, 300))}
                  placeholder={t('booking.manager.reasonPlaceholder')}
                  className="w-full rounded-md border border-rose-200 dark:border-rose-800/60 bg-white/80 dark:bg-white/5 px-3 py-2 text-sm"
                />
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={() => setConfirmOpen(false)}>
                  {t('booking.common.back')}
                </Button>
                <Button
                  color="danger"
                  className="text-white"
                  startContent={<Icon icon="mdi:calendar-remove" className="text-[18px]" />}
                  isLoading={cancelling}
                  onPress={doCancel}
                >
                  {t('booking.common.cancelAppointment')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
